import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { 
  PenTool, Layers, MousePointer2, Sparkles, 
  Zap, ArrowRight, Play, Star, Sliders
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Process from "../components/Process";
import SEO from "../components/SEO";
import DesignWorkshop from "../components/DesignWorkshop";
import SectionStepper from "../components/SectionStepper";

const designSteps = [
  {
    step: "01",
    title: "Discovery",
    desc: "Understanding the user needs, business goals, and current pain points through deep research.",
    image: "https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "Wireframing",
    desc: "Mapping out the structure and user flow without the distraction of visual aesthetics.",
    image: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&w=1200"
  },
  {
    step: "03",
    title: "High-Fidelity Design",
    desc: "Creating pixel-perfect interfaces that combine usability with cinematic visual impact.",
    image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Prototyping",
    desc: "Building interactive models to test functionality and motion before a single line of code is written.",
    image: "https://images.pexels.com/photos/1092670/pexels-photo-1092670.jpeg?auto=compress&cs=tinysrgb&w=1200"
  }
];

const UIUXDesign = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const stepperSections = [
    { id: "hero", label: "Elegance" },
    { id: "capabilities", label: "Capabilities" },
    { id: "process", label: "Philosophy" },
    { id: "workshop", label: "Design Lab" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Human-Centered UI/UX Design" 
        description="Premium UI/UX design services. We build intuitive, cinematic interfaces that users love to touch."
      />
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* Hero */}
        <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/8051636/8051636-uhd_1440_2560_24fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Human-Centered
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Design for <br />
                <span className="italic font-serif-accent gold-text">Elegance</span>
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                We believe a great interface is invisible. We focus on the intersection of human psychology and cinematic aesthetics to create designs that feel natural and premium.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Design Your Legacy <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#workshop" className="btn-ghost">
                  Enter Workshop
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="Capabilities" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Intuitive <span className="italic gold-text">Interfaces</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <PenTool />, title: "Product Design", desc: "End-to-end design for complex web and mobile applications." },
                { icon: <Layers />, title: "Design Systems", desc: "Scalable visual languages that ensure consistency across your brand." },
                { icon: <Sliders />, title: "Interaction Design", desc: "Focusing on the micro-interactions that make an app feel alive." }
              ].map((s, i) => (
                <div key={i} className="p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 group">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-4">{s.title}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Process id="process" steps={designSteps} title="The Design Philosophy" kicker={{ index: "02", label: "The Mindset" }} />

        <DesignWorkshop />
      </main>

      <Footer />
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default UIUXDesign;
